import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Euro, MoreHorizontal, Pencil, Trash2, Link2 } from "lucide-react";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import { useI18n } from '@/hooks/useI18n';
import { getStorageThumbnailUrl } from "@/utils/imageStorage";
import { OrphanItinerary } from "@/hooks/useOrphanItineraries";

interface OrphanItineraryCardProps {
  itinerary: OrphanItinerary;
  className?: string;
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onLink?: (itineraryId: string, title: string) => void;
}

export const OrphanItineraryCard = ({
  itinerary,
  className,
  onEdit,
  onDelete,
  onLink,
}: OrphanItineraryCardProps) => {
  const { t } = useI18n();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleEdit = () => {
    if (!onEdit) return;
    onEdit(itinerary.id);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(itinerary.id);
  };

  const handleLink = () => {
    if (!onLink) return;
    onLink(itinerary.id, itinerary.title);
  };

  return (
    <Card
      className={cn(
        "border border-gray-300 rounded-2xl overflow-hidden transition-all cursor-pointer",
        isDropdownOpen
          ? "-translate-y-2 scale-105 shadow-xl"
          : "hover:-translate-y-2 hover:scale-105 hover:shadow-xl",
        className,
      )}
      onClick={handleEdit}
    >
      <CardContent className="p-0 h-full flex flex-col relative">
        {/* Image Header */}
        <div className="relative w-full h-[150px]">
          {/* Background Image */}
          <div
            className={cn(
              "absolute inset-0 bg-cover bg-center transition-all rounded-tl rounded-tr",
              itinerary.image ? "bg-gray-200" : "bg-button-primary",
            )}
            style={{
              backgroundImage: itinerary.image
                ? `url(${getStorageThumbnailUrl(itinerary.image, { noTransform: true })})`
                : undefined,
            }}
          />

          {/* Logo placeholder quando non c'è immagine */}
          {!itinerary.image && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <img
                src="/assets/images/light_extended.png"
                alt="Place2Be Logo"
                className="max-w-[70%] max-h-[70%] object-contain"
              />
            </div>
          )}

          {/* Badge status */}
          <div className="absolute top-3 left-3">
            <ItineraryStatusBadge
              status={itinerary.status}
              hideDraftQuote={true}
            />
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2">
            {/* Menu Button */}
            <DropdownMenu onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                  title="Menu"
                >
                  <MoreHorizontal className="h-5 w-5 text-gray-800" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {t("common.edit")}
                </DropdownMenuItem>
                
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLink();
                  }}
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  {t("playground.linkToRequest")}
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  className="text-red-600 focus:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col">
          {/* Title */}
          <h4 className="text-black mb-4 line-clamp-1">
            {itinerary.title || t("itinerary.untitledItinerary")}
          </h4>

          {/* Trip Info Row */}
          <div className="flex items-center justify-between mb-2 text-sm">
            {/* Dates */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">
                {formatTravelDates(itinerary.start_date, itinerary.end_date)}
              </span>
            </div>

            {/* Budget */}
            {itinerary.budget && (
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {itinerary.budget.toLocaleString('it-IT')}
                </span>
              </div>
            )}
          </div>

        </div>
      </CardContent>
    </Card>
  );
};
